<template>
  <div class="home">
    <div>
      <!-- header -->
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{$t('employees')}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search')">
              </div>
              <!-- Mobile filter toggle -->
              <button @click="showFilters = !showFilters" class="btn btn-outline-secondary d-md-none">
                <AppIcon name="bi-funnel" class="bi bi-funnel" />
              </button>
            </div>
          </div>
        </div>
      </section>
      <!-- header -->

      <!-- table -->
      <section class="">
        <div class="px-8">
          <!-- Mobile action buttons -->
          <div class="d-flex d-md-none gap-2 mb-3">
            <button v-if="helper.checkAccess('create_employees') != -1" @click="create" class="btn btn-sm btn-success flex-grow-1">
              <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
              <span>{{$t('employee')}}</span>
            </button>
          </div>

          <!-- Filters section (collapsible on mobile, always visible on desktop) -->
          <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
            <!-- Department filter -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('status')}}</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">{{$t('all_statuses')}}</option>
                <option value="active">{{$t('active')}}</option>
                <option value="inactive">{{$t('inactive')}}</option>
                <option value="on_leave">{{$t('on_leave')}}</option>
              </select>
            </div>
            
            <!-- Spacer for desktop -->
            <div class="col d-none d-md-block"></div>
            
            <!-- Status filter -->
            <div class="col-12 col-md-3 col-lg-2">
            </div>
            
            <!-- Desktop action buttons -->
            <div class="col-md-2 d-none d-md-block" v-if="helper.checkAccess('create_employees') != -1">
              <button @click="create" class="btn btn-sm btn-success w-100">
                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                <span>{{$t('employee')}}</span>
              </button>
            </div>
          </div>

          <div class="page-table-container mb-6 bg-white rounded shadow">
            
            <!-- table -->
            <div class="table-responsive">
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <sortable-table-header field="reference" label="reference" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchEmployees)" />
                    <sortable-table-header field="name" label="name" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchEmployees)" />
                    <sortable-table-header field="position" label="position" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchEmployees)" />
                    <sortable-table-header field="department" label="department" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchEmployees)" />
                    <th class="pt-4 pb-3 px-6">{{$t('email')}}</th>
                    <th class="pt-4 pb-3 px-6">{{$t('phone')}}</th>
                    <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchEmployees)" />
                    <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="employee in employees" :key="employee.id">
                    <td class="py-5 px-6"><router-link :to="`/hr/employees/${employee.id}`"><b>{{employee.reference}}</b></router-link></td>
                    <td class="py-5 px-6">{{employee.name}}</td>
                    <td class="py-5 px-6">{{employee.position}}</td>
                    <td class="py-5 px-6">{{employee.department}}</td>
                    <td class="py-5 px-6">{{employee.email}}</td>
                    <td class="py-5 px-6">{{employee.phone}}</td>
                    <td class="py-5 px-6">
                      <span class="badge" :class="{
                        'bg-success': employee.status === 'active',
                        'bg-secondary': employee.status === 'inactive',
                        'bg-warning': employee.status === 'on_leave'
                      }">
                        {{$t(employee.status)}}
                      </span>
                    </td>
                    <td class="py-5 px-6">
                      <button @click="view(employee)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-eye" class="bi bi-eye" />
                        {{$t('details')}}
                      </button>
                      <button v-if="helper.checkAccess('edit_employees') != -1" @click="edit(employee)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                        {{$t('edit')}}
                      </button>
                      <button v-if="helper.checkAccess('delete_employees') != -1" @click="deleteEmployee(employee)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                        {{$t('delete')}}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="total > employees.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
              <compact-pagination :pages="pagination" :current-page="currentPage" @change="changePage" />
            </div>

            <table-loading-bar v-if="loading" class="page-table-state text-center" />
            <div v-else-if="!employees.length && !isFiltered" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
              <AppIcon name="bi-people" class="bi bi-people text-muted" style="font-size: 3rem;" />
              <h4 class="mt-3">{{ $t('no_employees_added') }}</h4>
              <p class="text-muted">{{ $t('start_by_adding_employees') }}</p>
              <template v-if="helper.checkAccess('create_employees') != -1">
                  <button class="btn btn-outline-primary mt-2" @click="create">
                      {{ $t('create_first_employee') }}
                  </button>
              </template>
            </div>
            <div v-else-if="!employees.length && isFiltered" class="page-table-state text-center" style="padding:1rem">{{$t('no_employees_found')}}</div>
          </div>
          <!-- table -->

          <div class="col-sm-7">
            {{$t('show')}}
            <select v-model="perPage" class="form-select mb-3" name="Resulats" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            {{$t('entities')}}
          </div>
        </div>
      </section>
    </div>

    <!-- Employee Modal -->
    <div class="modal fade" id="employeeModal" tabindex="-1" aria-labelledby="employeeModal" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{isEdit ? $t('edit_employee') : $t('create_employee')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body" style="text-align:left">
            <ul class="nav nav-tabs mb-4" role="tablist">
              <li class="nav-item">
                <a class="nav-link active" data-bs-toggle="tab" href="#personal">{{$t('personal_details')}}</a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#job">{{$t('job_details')}}</a>
              </li>
            </ul>

            <div class="tab-content">
              <!-- Personal Details Tab -->
              <div class="tab-pane fade show active" id="personal">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="nameInput" class="form-label text-dark">{{$t('name')}} *</label>
                    <input v-model="form.name" type="text" class="form-control" id="nameInput" required>
                  </div>
                  <div class="col-md-6">
                    <label for="emailInput" class="form-label text-dark">{{$t('email')}}</label>
                    <input v-model="form.email" type="email" class="form-control" id="emailInput">
                  </div>
                  <div class="col-md-6">
                    <label for="phoneInput" class="form-label text-dark">{{$t('phone')}}</label>
                    <input v-model="form.phone" type="text" class="form-control" id="phoneInput">
                  </div>
                  <div class="col-md-6">
                    <label for="dobInput" class="form-label text-dark">{{$t('date_of_birth')}}</label>
                    <input v-model="form.date_of_birth" type="date" class="form-control" id="dobInput">
                  </div>
                  <div class="col-md-6">
                    <label for="genderInput" class="form-label text-dark">{{$t('gender')}}</label>
                    <select v-model="form.gender" class="form-select" id="genderInput">
                      <option value="">{{$t('select')}}</option>
                      <option value="male">{{$t('male')}}</option>
                      <option value="female">{{$t('female')}}</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label for="nationalityInput" class="form-label text-dark">{{$t('nationality')}}</label>
                    <input v-model="form.nationality" type="text" class="form-control" id="nationalityInput">
                  </div>
                  <div class="col-md-6">
                    <label for="nationalIdInput" class="form-label text-dark">{{$t('national_id')}}</label>
                    <input v-model="form.national_id" type="text" class="form-control" id="nationalIdInput">
                  </div>
                  <div class="col-md-6">
                    <label for="cnssNumberInput" class="form-label text-dark">N° CNSS</label>
                    <input v-model="form.cnss_number" type="text" maxlength="100" class="form-control" id="cnssNumberInput">
                  </div>
                  <div class="col-md-6">
                    <label for="dependentsInput" class="form-label text-dark">Personnes à charge</label>
                    <input v-model.number="form.dependents_count" type="number" min="0" class="form-control" id="dependentsInput">
                  </div>
                  <div class="col-md-12">
                    <label for="addressInput" class="form-label text-dark">{{$t('address')}}</label>
                    <textarea v-model="form.address" class="form-control" id="addressInput" rows="2"></textarea>
                  </div>
                  <div class="col-md-6">
                    <label for="emergencyNameInput" class="form-label text-dark">{{$t('emergency_contact_name')}}</label>
                    <input v-model="form.emergency_contact_name" type="text" class="form-control" id="emergencyNameInput">
                  </div>
                  <div class="col-md-6">
                    <label for="emergencyPhoneInput" class="form-label text-dark">{{$t('emergency_contact_phone')}}</label>
                    <input v-model="form.emergency_contact_phone" type="text" class="form-control" id="emergencyPhoneInput">
                  </div>
                </div>
              </div>

              <!-- Job Details Tab -->
              <div class="tab-pane fade" id="job">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="positionInput" class="form-label text-dark">{{$t('position')}}</label>
                    <input v-model="form.position" type="text" class="form-control" id="positionInput">
                  </div>
                  <div class="col-md-6">
                    <label for="employmentTypeInput" class="form-label text-dark">{{$t('employment_type')}}</label>
                    <select v-model="form.employment_type" class="form-select" id="employmentTypeInput">
                      <option value="">{{$t('select')}}</option>
                      <option value="full-time">{{$t('full_time')}}</option>
                      <option value="part-time">{{$t('part_time')}}</option>
                      <option value="contract">{{$t('contract')}}</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label for="hireDateInput" class="form-label text-dark">{{$t('hire_date')}}</label>
                    <input v-model="form.hire_date" type="date" class="form-control" id="hireDateInput">
                  </div>
                  <div class="col-md-6">
                    <label for="salaryInput" class="form-label text-dark">{{$t('salary')}}</label>
                    <input v-model="form.salary" type="number" min="0" step="0.01" class="form-control" id="salaryInput">
                  </div>
                  <div class="col-md-6">
                    <label for="currencyInput" class="form-label text-dark">{{$t('salary_currency')}}</label>
                    <input v-model="form.salary_currency" type="text" maxlength="10" class="form-control" id="currencyInput" placeholder="USD">
                  </div>
                  <div class="col-md-6">
                    <label for="statusInput" class="form-label text-dark">{{$t('status')}}</label>
                    <select v-model="form.status" class="form-select" id="statusInput">
                      <option value="active">{{$t('active')}}</option>
                      <option value="inactive">{{$t('inactive')}}</option>
                      <option value="on_leave">{{$t('on_leave')}}</option>
                      <option value="terminated">{{$t('terminated')}}</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label for="annualLeaveInput" class="form-label text-dark">{{$t('annual_leave_balance')}}</label>
                    <input v-model="form.annual_leave_balance" type="number" min="0" step="0.5" class="form-control" id="annualLeaveInput">
                  </div>
                  <div class="col-md-6">
                    <label for="sickLeaveInput" class="form-label text-dark">{{$t('sick_leave_balance')}}</label>
                    <input v-model="form.sick_leave_balance" type="number" min="0" step="0.5" class="form-control" id="sickLeaveInput">
                  </div>
                  <div class="col-md-12">
                    <label for="scheduleInput" class="form-label text-dark">{{$t('work_schedule')}}</label>
                    <textarea v-model="form.work_schedule" class="form-control" id="scheduleInput" rows="2" placeholder="Mon-Fri 9:00-17:00"></textarea>
                  </div>
                  <div class="col-md-12">
                    <collapsible-notes-field v-model="form.notes" id="employee-notes" rows="3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
            <button type="button" class="btn btn-success" :disabled="loading" @click="save"><AppIcon name="bi-check-circle" class="bi bi-check-circle me-2" />{{isEdit ? $t('edit') : $t('create')}}</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Employee modal -->
  </div>
</template>

<script>
import helper from '@/helper'
import sortableList from '@/mixins/sortableList'

export default {
  name: 'Employee',
  persistedFilterState: {
    key: 'employees',
    fields: ['search', 'filters', 'perPage', 'sortField', 'sortDirection'],
  },
  mixins: [sortableList],
  components: {
  },
  data() {
    return {
      helper,
      employees: [],
      search: '',
      showFilters: false,
      filters: {
        department: '',
        status: ''
      },
      currentPage: 1,
      perPage: 10,
      total: 0,
      isEdit: false,
      form: this.getEmptyForm(),
      modal: null,
      loading: false,
      pagination: []
    }
  },
  watch: {
    search() {
      this.fetchEmployees()
    },
    'filters.department'() {
      this.fetchEmployees()
    },
    'filters.status'() {
      this.fetchEmployees()
    },
    perPage() {
      this.fetchEmployees()
    }
  },
  computed: {
    isFiltered() {
      return (this.search && this.search.length > 0) ||
             (this.filters.department && this.filters.department !== '') ||
             (this.filters.status && this.filters.status !== '');
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_employees') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_employees') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchEmployees()
    // eslint-disable-next-line no-undef
    this.modal = new bootstrap.Modal(document.getElementById('employeeModal'), {backdrop: 'static', keyboard: false})
    
    // Check if we need to open edit modal from route params
    if (this.$route.params.editId) {
      this.editEmployeeById(this.$route.params.editId)
    }
  },
  methods: {
    getEmptyForm() {
      return {
        name: '',
        email: '',
        phone: '',
        address: '',
        date_of_birth: '',
        gender: '',
        nationality: '',
        national_id: '',
        cnss_number: '',
        dependents_count: 0,
        emergency_contact_name: '',
        emergency_contact_phone: '',
        position: '',
        department: '',
        employment_type: '',
        hire_date: '',
        salary: '',
        salary_currency: this.$store.state.business?.currency || 'DH',
        work_schedule: '',
        status: 'active',
        annual_leave_balance: 0,
        sick_leave_balance: 0,
        notes: ''
      }
    },

    async fetchEmployees() {
      try {
        this.loading = true
        this.employees = []
        
        const params = new URLSearchParams({ page: this.currentPage, per_page: this.perPage })
        this.appendSortParams(params)
        if (this.search) params.append('keyword', this.search)
        if (this.filters.department) params.append('department', this.filters.department)
        if (this.filters.status) params.append('status', this.filters.status)
        const url = `${process.env.VUE_APP_API}/employees?${params.toString()}`

        const response = await helper.customGet(url)
        
        // The API returns data in response.data format
        this.employees = response.data.data
        this.total = response.data.count
        
        // Build pagination array
        this.pagination = []
        let maxPagination = Math.ceil(this.total / this.perPage)
        for(let i = 1; i <= maxPagination; i++){
          this.pagination.push(i)
        }
        
        this.loading = false
      } catch (error) {
        this.$swal({ icon: 'error', title: this.$t('error'), text: error.message })
        this.employees = []
        this.total = 0
        this.loading = false
      }
    },
    create() {
      this.isEdit = false
      this.form = this.getEmptyForm()
      this.modal.show()
    },
    edit(employee) {
      this.isEdit = true
      this.form = { ...employee }
      this.modal.show()
    },
    view(employee) {
      this.$router.push(`/hr/employees/${employee.id}`)
    },
    async save() {
      try {
        this.loading = true
        if (this.isEdit) {
          await helper.customPatch(`${process.env.VUE_APP_API}/employees/${this.form.id}`, this.form, {showToast: true})
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/employees`, this.form, {showToast: true})
        }
        this.loading = false
        this.clearForm()
        this.modal.hide()
        this.fetchEmployees()
      } catch (error) {
        this.loading = false
      }
    },
    clearForm() {
      this.form = this.getEmptyForm()
    },
    async deleteEmployee(employee) {
      const result = await this.$swal.fire({
        title: this.$t('are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('delete')
      })
      
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/employees/${employee.id}`)
          this.employees = helper.withoutDeletedRecords(this.employees, employee.id)
          this.total = Math.max(0, this.total - 1)
        } catch (error) {
          this.$swal({ icon: 'error', title: this.$t('error'), text: error.message })
        }
      }
    },
    changePage(page) {
      this.currentPage = page
      this.fetchEmployees()
    },
    async editEmployeeById(employeeId) {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employees/${employeeId}`)
        const employee = response.data.employee || response.data
        this.edit(employee)
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: 'Failed to load employee for editing'
        })
      }
    }
  }
}
</script>

<style scoped>
.nav-tabs .nav-link.active {
  background-color: #fff;
  border-bottom-color: transparent;
}
</style>
